import { useEffect, useRef, useState } from "react";
import {
  ZOOM_MIN,
  ZOOM_MAX,
  DEFAULT_FRAMING,
  framingStyle,
  detailAspect,
  normalizeFraming,
} from "../../utilities/framing";

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

// One WYSIWYG preview pane: drag to pan the focal point, slider to zoom.
// Renders the image exactly like FramedImage does so what the admin sees
// here is what each surface will show.
function FramingPane({
  label,
  aspectRatio,
  maxWidth,
  imageUrl,
  value,
  onChange,
  showTextGradient,
  picker,
}) {
  const boxRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Latest framing value for the zoom-repeat interval and manual input,
  // which outlive the render they were created in
  const valueRef = useRef(value);
  valueRef.current = value;
  const repeatRef = useRef(null);
  // Free-typed zoom text; null means "mirror the committed value"
  const [zoomDraft, setZoomDraft] = useState(null);

  function handlePointerDown(evt) {
    evt.preventDefault();
    try {
      evt.currentTarget.setPointerCapture(evt.pointerId);
    } catch {
      // No active pointer (e.g. synthetic events) — dragging still works
      // as long as the pointer stays over the pane
    }
    dragRef.current = {
      startX: evt.clientX,
      startY: evt.clientY,
      x: value.x,
      y: value.y,
    };
    setIsDragging(true);
  }

  function handlePointerMove(evt) {
    if (!dragRef.current) return;
    const box = boxRef.current?.getBoundingClientRect();
    const img = imgRef.current;
    if (!box || !img?.naturalWidth || !img?.naturalHeight) return;
    // Pointer delta maps to focal-point delta via the pannable slack
    // (zoomed cover-fit size minus the pane), so the image tracks the
    // cursor 1:1 at any zoom level
    const imageRatio = img.naturalWidth / img.naturalHeight;
    const boxRatio = box.width / box.height;
    const baseWidth =
      imageRatio > boxRatio ? box.height * imageRatio : box.width;
    const baseHeight =
      imageRatio > boxRatio ? box.height : box.width / imageRatio;
    const overflowX = value.zoom * baseWidth - box.width;
    const overflowY = value.zoom * baseHeight - box.height;
    const dx =
      overflowX > 1
        ? ((evt.clientX - dragRef.current.startX) * 100) / overflowX
        : 0;
    const dy =
      overflowY > 1
        ? ((evt.clientY - dragRef.current.startY) * 100) / overflowY
        : 0;
    onChange({
      ...value,
      x: clamp(dragRef.current.x - dx, 0, 100),
      y: clamp(dragRef.current.y - dy, 0, 100),
    });
  }

  function handlePointerEnd() {
    dragRef.current = null;
    setIsDragging(false);
  }

  // ± buttons nudge zoom in 0.01 steps and auto-repeat while held;
  // rounding to two decimals keeps float drift out of the readout
  function stepZoom(direction) {
    const current = valueRef.current;
    const next = Math.round((current.zoom + direction * 0.01) * 100) / 100;
    onChange({ ...current, zoom: clamp(next, ZOOM_MIN, ZOOM_MAX) });
  }

  function stopZoomRepeat() {
    if (!repeatRef.current) return;
    clearTimeout(repeatRef.current.timeout);
    clearInterval(repeatRef.current.interval);
    repeatRef.current = null;
  }

  function startZoomRepeat(direction) {
    stopZoomRepeat();
    stepZoom(direction);
    const timeout = setTimeout(() => {
      repeatRef.current.interval = setInterval(() => stepZoom(direction), 50);
    }, 400);
    repeatRef.current = { timeout, interval: null };
  }

  useEffect(() => stopZoomRepeat, []); // don't let a held button outlive the pane

  function handleZoomInput(evt) {
    const raw = evt.target.value;
    setZoomDraft(raw);
    const parsed = Number(raw);
    if (raw !== "" && parsed >= ZOOM_MIN && parsed <= ZOOM_MAX) {
      onChange({ ...valueRef.current, zoom: parsed });
    }
  }

  function commitZoomDraft() {
    if (zoomDraft !== null && zoomDraft !== "") {
      const parsed = Number(zoomDraft);
      if (!Number.isNaN(parsed)) {
        const rounded = Math.round(parsed * 100) / 100;
        onChange({
          ...valueRef.current,
          zoom: clamp(rounded, ZOOM_MIN, ZOOM_MAX),
        });
      }
    }
    setZoomDraft(null);
  }

  function handleKeyDown(evt) {
    const step = evt.shiftKey ? 5 : 1;
    const moves = {
      ArrowLeft: { x: value.x + step },
      ArrowRight: { x: value.x - step },
      ArrowUp: { y: value.y + step },
      ArrowDown: { y: value.y - step },
    };
    const move = moves[evt.key];
    if (!move) return;
    evt.preventDefault();
    onChange({
      ...value,
      x: clamp(move.x ?? value.x, 0, 100),
      y: clamp(move.y ?? value.y, 0, 100),
    });
  }

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <p className="text-xs font-bold text-ink/60">{label}</p>
        <button
          type="button"
          onClick={() => onChange({ ...DEFAULT_FRAMING })}
          className="text-xs text-ink/40 hover:text-ink underline transition-colors"
        >
          Reset
        </button>
      </div>
      {picker}
      <div
        ref={boxRef}
        role="application"
        aria-label={`${label} framing — drag to position the image, arrow keys to nudge`}
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onKeyDown={handleKeyDown}
        className={`relative overflow-hidden rounded-md border border-ink/15 bg-cream select-none focus:outline-none focus:ring-2 focus:ring-seal/40 ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        style={{ aspectRatio, maxWidth, touchAction: "none" }}
      >
        <img
          ref={imgRef}
          src={imageUrl}
          alt=""
          draggable={false}
          className="pointer-events-none"
          style={framingStyle(value)}
        />
        {showTextGradient && (
          <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-ink/70 to-transparent pointer-events-none" />
        )}
        {/* Rule-of-thirds grid; difference blend keeps it visible on any scan */}
        <div
          className={`absolute inset-0 pointer-events-none mix-blend-difference transition-opacity duration-150 ${
            isDragging ? "opacity-80" : "opacity-30"
          }`}
        >
          <div className="absolute inset-y-0 left-1/3 w-px bg-white" />
          <div className="absolute inset-y-0 left-2/3 w-px bg-white" />
          <div className="absolute inset-x-0 top-1/3 h-px bg-white" />
          <div className="absolute inset-x-0 top-2/3 h-px bg-white" />
        </div>
      </div>
      {/* Controls span the full form width — a slider squeezed to the
          tile preview's 235px is too coarse to pick a zoom value */}
      <div className="flex items-center gap-2 mt-2">
        <span className="font-mono text-[10px] uppercase tracking-wide text-ink/40">
          Zoom
        </span>
        <button
          type="button"
          onPointerDown={(evt) => {
            evt.preventDefault();
            startZoomRepeat(-1);
          }}
          onPointerUp={stopZoomRepeat}
          onPointerLeave={stopZoomRepeat}
          onPointerCancel={stopZoomRepeat}
          onClick={(evt) => {
            // detail === 0 means a keyboard "click" — pointer presses have
            // already stepped in onPointerDown
            if (evt.detail === 0) stepZoom(-1);
          }}
          aria-label={`${label} zoom out`}
          className="w-6 h-6 shrink-0 rounded border border-ink/25 text-ink/60 hover:text-ink hover:border-ink/60 transition-colors leading-none"
        >
          −
        </button>
        <input
          type="range"
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          step={0.01}
          value={value.zoom}
          onChange={(evt) =>
            onChange({ ...value, zoom: Number(evt.target.value) })
          }
          aria-label={`${label} zoom`}
          className="flex-1 min-w-0 accent-seal"
        />
        <button
          type="button"
          onPointerDown={(evt) => {
            evt.preventDefault();
            startZoomRepeat(1);
          }}
          onPointerUp={stopZoomRepeat}
          onPointerLeave={stopZoomRepeat}
          onPointerCancel={stopZoomRepeat}
          onClick={(evt) => {
            if (evt.detail === 0) stepZoom(1);
          }}
          aria-label={`${label} zoom in`}
          className="w-6 h-6 shrink-0 rounded border border-ink/25 text-ink/60 hover:text-ink hover:border-ink/60 transition-colors leading-none"
        >
          +
        </button>
        <input
          type="number"
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          step={0.01}
          value={zoomDraft ?? value.zoom}
          onChange={handleZoomInput}
          onBlur={commitZoomDraft}
          onKeyDown={(evt) => {
            if (evt.key === "Enter") evt.target.blur();
          }}
          aria-label={`${label} zoom value`}
          className="w-16 shrink-0 rounded border border-ink/25 bg-transparent px-1.5 py-0.5 font-mono text-xs text-ink/70 text-right focus:outline-none focus:border-ink/60"
        />
      </div>
    </div>
  );
}

// Framing controls for the three display contexts. Tile and carousel only
// ever show the primary image; the detail modal cycles through all of them,
// so its pane has a thumbnail picker to choose which image to frame. The
// carousel pane only appears while the item is featured; its values persist
// either way.
// `captions` / `onCaptionChange` are optional (articles only): captions is
// [{url, caption}] keyed by image URL, and passing onCaptionChange adds a
// caption input under the detail pane for the selected image.
export default function FramingEditor({
  images,
  kind,
  tileVariant,
  featured,
  framing,
  imageFramings,
  onChange,
  onImageFramingsChange,
  captions,
  onCaptionChange,
}) {
  // Clamped so removing images can't leave the selection out of range
  const [detailIndex, setDetailIndex] = useState(0);
  const selectedIndex = Math.min(detailIndex, images.length - 1);
  const primaryUrl = images[0];
  const selectedUrl = images[selectedIndex];

  const isWideTile = kind === "cover" && tileVariant.startsWith("landscape");
  const tileHasText =
    (kind === "cover" && !tileVariant.endsWith("-image")) ||
    (kind === "stamp" && tileVariant === "card") ||
    (kind === "article" && tileVariant === "image-card");
  // Article text tiles show no image, so tile framing doesn't apply there
  const tileIsFramed =
    kind !== "article" || ["image", "image-card"].includes(tileVariant);

  function updateContext(context, next) {
    onChange({ ...framing, [context]: next });
  }

  // The primary image's detail framing lives in framing.detail; secondary
  // images each keep their own entry in imageFramings, keyed by URL
  const detailValue =
    selectedIndex === 0
      ? framing.detail
      : normalizeFraming(
          imageFramings.find((entry) => entry.url === selectedUrl),
        );

  function updateDetail(next) {
    if (selectedIndex === 0) {
      updateContext("detail", next);
      return;
    }
    const rest = imageFramings.filter((entry) => entry.url !== selectedUrl);
    onImageFramingsChange([...rest, { url: selectedUrl, ...next }]);
  }

  return (
    <div>
      <p className="text-sm font-bold text-ink/80 mb-1">Image framing</p>
      <p className="text-xs text-ink/40 mb-3">
        Drag each preview to position the scan; use the slider to zoom.
      </p>
      <div className="space-y-5">
        {/* Same ratios as the mosaic (235:300 single, 486:300 wide), so the
            cover crop shown here matches the homepage tile exactly */}
        {tileIsFramed && (
          <FramingPane
            label="Homepage tile"
            aspectRatio={isWideTile ? "486 / 300" : "235 / 300"}
            maxWidth={isWideTile ? "486px" : "235px"}
            imageUrl={primaryUrl}
            value={framing.tile}
            onChange={(next) => updateContext("tile", next)}
            showTextGradient={tileHasText}
          />
        )}
        {/* key resets the pane's drag/zoom-draft state when switching images */}
        <div>
          <FramingPane
            key={selectedUrl}
            label={
              images.length > 1
                ? `Detail view — image ${selectedIndex + 1} of ${images.length}`
                : "Detail view"
            }
            aspectRatio={detailAspect(kind)}
            maxWidth="28rem"
            imageUrl={selectedUrl}
            value={detailValue}
            onChange={updateDetail}
            picker={
              images.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {images.map((url, i) => (
                    <button
                      key={url}
                      type="button"
                      onClick={() => setDetailIndex(i)}
                      aria-label={`Frame image ${i + 1} in the detail view`}
                      aria-pressed={i === selectedIndex}
                      className={`relative h-12 w-12 shrink-0 overflow-hidden rounded border-2 transition-colors ${
                        i === selectedIndex
                          ? "border-seal"
                          : "border-transparent hover:border-ink/40"
                      }`}
                    >
                      <img
                        src={url}
                        alt=""
                        draggable={false}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )
            }
          />
          {/* Caption for the selected image, shown as a pill over it in the
            detail carousel. Lives here rather than on the tiny thumbnail
            cards so there's room to read and write it. */}
          {onCaptionChange && (
            <div className="mt-3" style={{ maxWidth: "28rem" }}>
              <input
                type="text"
                value={
                  captions?.find((entry) => entry.url === selectedUrl)
                    ?.caption || ""
                }
                onChange={(evt) =>
                  onCaptionChange(selectedUrl, evt.target.value)
                }
                placeholder={
                  images.length > 1
                    ? `Caption for image ${selectedIndex + 1} (optional)`
                    : "Caption (optional)"
                }
                aria-label={`Caption for image ${selectedIndex + 1}`}
                className="w-full border-b border-ink/25 bg-transparent py-1.5 text-sm text-ink outline-none transition-colors focus:border-ink placeholder:text-ink/40"
              />
              <p className="mt-1 text-xs text-ink/40">
                Shown over this image in the article&rsquo;s image carousel.
              </p>
            </div>
          )}
        </div>
        {featured && (
          <FramingPane
            label="Featured carousel"
            aspectRatio="1280 / 745"
            imageUrl={primaryUrl}
            value={framing.carousel}
            onChange={(next) => updateContext("carousel", next)}
          />
        )}
      </div>
    </div>
  );
}
